import type { ColumnDef } from '@tanstack/react-table'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import type { ChronicConditionRecord, LabResultRecord } from '../types'

const Unavailable = () => <span className="text-muted-foreground">-</span>

function formatClinicalDataDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(value)
  if (!match) return value

  const [, yearValue, monthValue, dayValue] = match
  const year = Number(yearValue)
  const month = Number(monthValue)
  const day = Number(dayValue)
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return value
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export const labResultsColumns: ColumnDef<LabResultRecord>[] = [
  {
    accessorKey: 'patient_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client" />
    ),
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: 'test_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Test" />
    ),
    cell: ({ row }) => row.original.test_name ?? <Unavailable />,
    enableSorting: false,
  },
  {
    accessorKey: 'result_raw',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Result" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'observed_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Observed" />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatClinicalDataDate(row.original.observed_at)}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'test_code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Test Code" />
    ),
    cell: ({ row }) => row.original.test_code ?? <Unavailable />,
    enableSorting: false,
  },
  {
    accessorKey: 'lab_vendor',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Lab" />
    ),
    cell: ({ row }) => row.original.lab_vendor ?? <Unavailable />,
    enableSorting: false,
  },
]

export const chronicConditionsColumns: ColumnDef<ChronicConditionRecord>[] = [
  {
    accessorKey: 'patient_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client" />
    ),
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: 'condition_description',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Condition" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'condition_code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Code" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'effective_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Effective Date" />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatClinicalDataDate(row.original.effective_date)}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'classification_system',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Code System" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'source_system',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    enableSorting: false,
  },
]
